/*eslint no-restricted-globals: 0*/
import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { getConfig } from 'tw-oi-core'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import api from 'tw-oi-core/services/ContentDelivery'
import {
  trackSearchResultsTopicClick,
  trackSearchKeyword,
  trackEmptyResultsSearchKeyword,
} from 'tw-oi-core/services/analytics'
import * as ContentsActions from 'tw-oi-core/actions/ContentsActions'

import { ROUTE, MESSAGE, DEFAULT_PUBLICATION_TYPE, BRANDS } from '../config'
import {
  getCollapsedSearchResults,
  isSupportedContent,
  isVideo,
  isArticlePublication,
  isPdf,
  getRootFolderResourceKey,
} from '../extras/utils'
import {
  buildBrowseGuideBreadcrumbs,
  isBrowseRootRoute,
} from '../extras/buildBrowseGuideBreadcrumbs'
import {
  buildBrowseSearchResultLink,
  clearBrowseSearchRestoreState,
  getBrowseSearchRestoreQuery,
} from '../extras/browseSearchOrigin'
import { getDownloadedCategories, getOfflineDownloadState } from '../services/offlineStorage'
import { removeDownloadedCategory } from '../services/offlineRemover'
import { isOffline, onConnectivityChange } from '../services/offlineDetect'
import {
  getOfflineDownloadSession,
  subscribeOfflineDownloadSession,
} from '../services/offlineDownloadSession'
import { isBrowseRoute } from './OfflineRouteRedirect'
import { getBrowseParentCategory, isSameBrowseOfflineSection } from '../extras/offlineBrowse'
import { findOfflineVehicleForRoute } from '../extras/loadOfflineGuideVehicle'

import BrowseGuideHeader from './BrowseGuideHeader'
import ScreenContainer from './ScreenContainer'
import SearchInput from './SearchInput'
import OfflineSaveBanner from './OfflineSaveBanner'
import BreadCrumb from './BreadCrumb'
import ContentList from './ContentList'
import Loader from './Loader'
import ErrorMessage from './ErrorMessage'

import '../styles/BrowseGuide.scss'
import '../styles/BreadCrumb.scss'

class BrowseGuideLayout extends Component {
  static propTypes = {
    match: PropTypes.object.isRequired,
    baseRoute: PropTypes.string.isRequired,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    showOfflineBanner: PropTypes.bool,
    breadcrumbItems: PropTypes.array,
    foldersIndex: PropTypes.object,
    rootFolderResourceKey: PropTypes.string,
    searchQuery: PropTypes.string,
    searchTopics: PropTypes.object,
    searchResults: PropTypes.array,
    errorSearch: PropTypes.object,
    fetching: PropTypes.bool,
    currentId: PropTypes.string,
    currentBrand: PropTypes.string,
    brandRoute: PropTypes.string,
    vehicleInfo: PropTypes.object,
    contents: PropTypes.object,
    ownersManualPdfs: PropTypes.object,
    offlineDownloadedCategories: PropTypes.object,
    offlineDownloadState: PropTypes.string,
    offlineVehicleId: PropTypes.string,
    onOfflineCategoriesChange: PropTypes.func,
    userMedia: PropTypes.shape({
      isDesktop: PropTypes.bool,
    }),
    ContentsActions: PropTypes.shape({
      setSearchQuery: PropTypes.func.isRequired,
      searchContentsByVehicleId: PropTypes.func.isRequired,
    }).isRequired,
    history: PropTypes.shape({
      location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
      }).isRequired,
      replace: PropTypes.func.isRequired,
    }).isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string,
      state: PropTypes.object,
    }),
  }

  static defaultProps = {
    showOfflineBanner: true,
    offlineDownloadedCategories: undefined,
    offlineDownloadState: 'none',
    offlineVehicleId: null,
    onOfflineCategoriesChange: null,
    brandRoute: '/',
  }

  state = {
    downloadedCategories: new Set(),
    downloadState: 'none',
    offline: isOffline(),
    offlineVehicleId: null,
    categoriesReady: !isOffline(),
  }

  unsubscribeDownloadSession = null
  subscribedDownloadVehicleId = null

  usesParentOfflineData() {
    return this.props.offlineDownloadedCategories !== undefined
  }

  getActiveDownloadedCategories() {
    return this.usesParentOfflineData()
      ? this.props.offlineDownloadedCategories
      : this.state.downloadedCategories
  }

  getDownloadState() {
    return this.usesParentOfflineData()
      ? this.props.offlineDownloadState
      : this.state.downloadState
  }

  getOfflineVehicleId() {
    const vehicleId = this.usesParentOfflineData()
      ? (this.props.offlineVehicleId || this.props.currentId)
      : (this.state.offlineVehicleId || this.props.currentId)

    return vehicleId != null && vehicleId !== '' ? String(vehicleId) : null
  }

  /**
   * Vehicle id used for offline download sessions. Prefers an id that already
   * has an in-flight download so remounts reconnect to the same session.
   *
   * @returns {string|null}
   */
  getDownloadSessionVehicleId() {
    const currentId = this.props.currentId != null && this.props.currentId !== ''
      ? String(this.props.currentId)
      : null
    const offlineId = this.getOfflineVehicleId()

    if (currentId && getOfflineDownloadSession(currentId).status === 'downloading') {
      return currentId
    }
    if (offlineId && getOfflineDownloadSession(offlineId).status === 'downloading') {
      return offlineId
    }

    return offlineId || currentId
  }

  loadOfflineCategories = async() => {
    if (this.usesParentOfflineData()) return

    const requestUrl = this.props.match.url
    const { baseRoute, brandRoute, currentBrand } = this.props

    if (isOffline()) {
      this.setState({ categoriesReady: false })
    }

    try {
      const record = await findOfflineVehicleForRoute({ baseRoute, brandRoute, currentBrand })
      if (this.props.match.url !== requestUrl) return

      const vehicleId = record ? String(record.vehicleId) : (this.props.currentId ? String(this.props.currentId) : null)
      if (!vehicleId) {
        this.setState({ categoriesReady: true })
        return
      }

      const [categories, downloadState] = await Promise.all([
        getDownloadedCategories(vehicleId),
        getOfflineDownloadState(vehicleId),
      ])

      if (this.props.match.url !== requestUrl) return

      this.setState(
        { downloadedCategories: categories, downloadState, offlineVehicleId: vehicleId, categoriesReady: true },
        () => {
          this.bindDownloadSession(this.getDownloadSessionVehicleId() || vehicleId)
          if (isOffline()) {
            this.validateBrowseOfflineRoute(categories)
          }
        }
      )
    } catch (e) {
      if (this.props.match.url !== requestUrl) return
      this.setState({ categoriesReady: true })
    }
  }

  componentDidMount() {
    this.applyBrowseSearchRestore()

    if (!this.usesParentOfflineData()) {
      this.loadOfflineCategories()
    } else if (isOffline()) {
      this.validateBrowseOfflineRoute(this.props.offlineDownloadedCategories)
    }

    this.bindDownloadSession(this.getDownloadSessionVehicleId())

    this.unsubscribeConnectivity = onConnectivityChange(online => {
      this.setState({ offline: !online })
      if (isBrowseRoute(this.props.history.location.pathname) && !this.usesParentOfflineData()) {
        this.loadOfflineCategories()
      }
    })
  }

  componentWillUnmount() {
    if (this.unsubscribeConnectivity) {
      this.unsubscribeConnectivity()
    }
    this.unbindDownloadSession()
  }

  applyBrowseSearchRestore = (prevProps) => {
    const { history, location, ContentsActions, searchQuery } = this.props
    const restoreQuery = getBrowseSearchRestoreQuery(location)

    if (!restoreQuery) {
      return
    }

    const prevRestoreQuery = prevProps ? getBrowseSearchRestoreQuery(prevProps.location) : null
    if (prevProps && prevRestoreQuery === restoreQuery && searchQuery === restoreQuery) {
      return
    }

    if (searchQuery !== restoreQuery) {
      ContentsActions.setSearchQuery(restoreQuery)
    }
    clearBrowseSearchRestoreState(history)
  }

  componentDidUpdate(prevProps) {
    const { searchQuery } = this.props

    this.applyBrowseSearchRestore(prevProps)

    if (prevProps.searchQuery !== searchQuery && searchQuery) {
      this.getSearchContents(searchQuery)
    }

    if (this.usesParentOfflineData()) {
      if (
        prevProps.offlineDownloadedCategories !== this.props.offlineDownloadedCategories &&
        isOffline()
      ) {
        this.validateBrowseOfflineRoute(this.props.offlineDownloadedCategories)
      }
    } else if (
      prevProps.currentId !== this.props.currentId ||
      prevProps.match.url !== this.props.match.url
    ) {
      const sameOfflineSection =
        prevProps.currentId === this.props.currentId &&
        isSameBrowseOfflineSection(prevProps.match, this.props.match, this.props.baseRoute)

      if (sameOfflineSection && this.state.downloadedCategories.size > 0) {
        if (isOffline()) {
          this.validateBrowseOfflineRoute(this.state.downloadedCategories)
        }
      } else {
        this.loadOfflineCategories()
      }
    }

    const prevVehicleId = this.subscribedDownloadVehicleId
    const nextVehicleId = this.getDownloadSessionVehicleId()

    if (prevVehicleId !== nextVehicleId) {
      this.bindDownloadSession(nextVehicleId)
    }
  }

  unbindDownloadSession = () => {
    if (this.unsubscribeDownloadSession) {
      this.unsubscribeDownloadSession()
      this.unsubscribeDownloadSession = null
    }
    this.subscribedDownloadVehicleId = null
  }

  bindDownloadSession = (vehicleId) => {
    const id = vehicleId != null && vehicleId !== '' ? String(vehicleId) : null
    if (id === this.subscribedDownloadVehicleId) return

    this.unbindDownloadSession()
    if (!id) return

    this.subscribedDownloadVehicleId = id
    this.unsubscribeDownloadSession = subscribeOfflineDownloadSession(id, this.handleDownloadSessionEvent)
  }

  handleDownloadSessionEvent = (event) => {
    if (!event) return

    // Banner owns downloading UI; layout only applies completion so categories
    // update even when the banner is unmounted (e.g. search or route change).
    if (event.type === 'complete') {
      this.handleDownloadComplete(event.downloadType, event.result)
    }
  }

  validateBrowseOfflineRoute(downloadedCategories) {
    const { history, match, baseRoute } = this.props

    if (!isOffline()) return
    if (!isBrowseRoute(history.location.pathname)) return
    if (isBrowseRootRoute(match, baseRoute)) return

    const parentCategory = getBrowseParentCategory(match, baseRoute)
    if (!parentCategory || !downloadedCategories.has(parentCategory)) {
      history.replace(`${baseRoute}${ROUTE.BROWSE}`)
    }
  }

  applyOfflineCategoryUpdate = ({ downloadedCategories, downloadState, offlineVehicleId }) => {
    const vehicleId = offlineVehicleId || this.getOfflineVehicleId()

    if (this.usesParentOfflineData()) {
      if (this.props.onOfflineCategoriesChange) {
        const update = { offlineVehicleId: vehicleId }
        if (downloadedCategories !== undefined) {
          update.downloadedCategories = downloadedCategories
        }
        if (downloadState !== undefined) {
          update.downloadState = downloadState
        }
        this.props.onOfflineCategoriesChange(update)
      }
      return
    }

    const stateUpdate = { offlineVehicleId: vehicleId }
    if (downloadedCategories !== undefined) {
      stateUpdate.downloadedCategories = downloadedCategories
    }
    if (downloadState !== undefined) {
      stateUpdate.downloadState = downloadState
    }
    this.setState(stateUpdate)
  }

  refreshOfflineCategories = async() => {
    const vehicleId = this.getOfflineVehicleId()
    if (!vehicleId) return

    const [categories, downloadState] = await Promise.all([
      getDownloadedCategories(vehicleId),
      getOfflineDownloadState(vehicleId),
    ])

    this.applyOfflineCategoryUpdate({
      downloadedCategories: categories,
      downloadState,
      offlineVehicleId: vehicleId,
    })
  }

  handleDownloadComplete = async(downloadType, result) => {
    const vehicleId = this.getOfflineVehicleId()
    if (!vehicleId) return

    if (downloadType === 'removed') {
      this.applyOfflineCategoryUpdate({
        downloadedCategories: new Set(),
        downloadState: 'none',
        offlineVehicleId: vehicleId,
      })
    } else if (downloadType) {
      const update = {
        downloadState: downloadType,
        offlineVehicleId: vehicleId,
      }

      if (result?.downloadedItems?.length) {
        update.downloadedCategories = new Set(result.downloadedItems)
      }

      this.applyOfflineCategoryUpdate(update)
    }

    await this.refreshOfflineCategories()
  }

  handleRemoveDownloadedCategory = async(categoryTitle) => {
    const { contents, ownersManualPdfs } = this.props
    const vehicleId = this.getOfflineVehicleId()
    if (!vehicleId || !categoryTitle) return

    try {
      await removeDownloadedCategory(vehicleId, categoryTitle, contents, ownersManualPdfs)
      await this.refreshOfflineCategories()
    } catch (e) {
      console.error('[offline] Failed to remove category:', categoryTitle, e)
    }
  }

  getSearchContents = searchQuery => {
    const { DEFAULT_LOCALE, REQUEST_ID_SEARCH } = getConfig()
    const { currentId, fetching, ContentsActions } = this.props

    if (this.searchRequest && fetching) {
      this.searchRequest = this.searchRequest.then(() =>
        ContentsActions.searchContentsByVehicleId(currentId, DEFAULT_LOCALE, searchQuery).then(() =>
          this.trackAnalyticsEvents()
        )
      )

      api.cancelRequest(REQUEST_ID_SEARCH)
    } else {
      this.searchRequest = ContentsActions.searchContentsByVehicleId(currentId, DEFAULT_LOCALE, searchQuery).then(() =>
        this.trackAnalyticsEvents()
      )
    }
  }

  trackAnalyticsEvents() {
    const { searchQuery, searchTopics } = this.props

    if (!searchQuery) {
      return
    }

    if (searchTopics && searchTopics.size === 0) {
      trackEmptyResultsSearchKeyword(searchQuery)
    }

    trackSearchKeyword(searchQuery)
  }

  addLinksToContent = item => {
    const { baseRoute, searchQuery, history } = this.props
    const returnPath = history.location.pathname
    const withSearchOrigin = pathname =>
      searchQuery ? buildBrowseSearchResultLink(pathname, searchQuery, returnPath) : pathname

    if (isArticlePublication(item)) {
      return {
        ...item,
        link: withSearchOrigin(`${baseRoute}${ROUTE.BROWSE}${ROUTE.ARTICLES}${ROUTE.CONTENT}/${item.resourceKey}`),
      }
    }

    if (isVideo(item)) {
      return {
        ...item,
        link: withSearchOrigin(`${baseRoute}${ROUTE.BROWSE}${ROUTE.VIDEOS}${ROUTE.CONTENT}/${item.resourceKey}`),
      }
    }

    if (isPdf(item)) {
      return {
        ...item,
        link: withSearchOrigin(`${baseRoute}${ROUTE.BROWSE}${ROUTE.PDFS}${ROUTE.CONTENT}/${item.resourceKey}`),
      }
    }

    return {
      ...item,
      link: withSearchOrigin(
        `${baseRoute}${ROUTE.BROWSE}${ROUTE.TOPIC}/${encodeURIComponent(item.resourceKey)}`
      ),
    }
  }

  handleSearchResultClick = item => {
    const { searchQuery, ContentsActions } = this.props

    trackSearchResultsTopicClick(item.title, searchQuery)
    // Clear so ArticleViewer (BrowseGuideLayout) shows content instead of the search overlay.
    ContentsActions.setSearchQuery('')
  }

  renderSearchResults = () => {
    const { searchQuery, errorSearch, fetching, searchResults, baseRoute } = this.props

    if (!searchQuery) {
      return null
    }

    if (errorSearch) {
      return (
        <ErrorMessage className='inverse inline' retryAction={() => this.getSearchContents(searchQuery)} />
      )
    }

    if (fetching) {
      return <Loader className='inverse' />
    }

    return (
      <Fragment>
        <div className='BrowseGuide-results-title'>
          {!searchResults ? MESSAGE.NO_RESULTS_FOUND : MESSAGE.SEARCH_RESULTS}
        </div>
        {searchResults && (
          <ContentList
            topics={searchResults.map(this.addLinksToContent)}
            searchQuery={searchQuery}
            baseRoute={baseRoute + ROUTE.BROWSE}
            onClick={this.handleSearchResultClick}
          />
        )}
      </Fragment>
    )
  }

  render() {
    const {
      match,
      searchQuery,
      ContentsActions,
      userMedia,
      children,
      showOfflineBanner,
      breadcrumbItems,
      baseRoute,
      foldersIndex,
      rootFolderResourceKey,
      contents,
      ownersManualPdfs,
    } = this.props
    const { offline, categoriesReady } = this.state
    const downloadedCategories = this.getActiveDownloadedCategories()
    const downloadState = this.getDownloadState()
    const sessionVehicleId = this.getDownloadSessionVehicleId()

    const breadcrumbs =
      breadcrumbItems ||
      buildBrowseGuideBreadcrumbs(match, baseRoute, {
        foldersIndex,
        rootFolderResourceKey,
        topics: contents?.topics,
        publications: contents?.publications,
      })
    const showBreadcrumb = !isBrowseRootRoute(match, baseRoute) && breadcrumbs.length > 0 && !searchQuery
    const isBrowseRoot = isBrowseRootRoute(match, baseRoute)
    const showOfflineSaveBanner =
      !searchQuery &&
      showOfflineBanner &&
      !isOffline() &&
      (userMedia.isDesktop || isBrowseRoot)

    const showChildren = !offline || this.usesParentOfflineData() || categoriesReady

    return (
      <div className='Topics BrowseGuide'>
        <BrowseGuideHeader />

        <div className='screen-content'>
          <div className='BrowseGuide-body'>
            <div className='UnifiedContentColumn'>
              <ScreenContainer currentUrl={match.url}>
                <div className='topics'>
                  <div className='BrowseGuide-toolbar'>
                    <div className='BrowseGuide-search'>
                      <SearchInput
                        searchQuery={searchQuery}
                        onChange={ContentsActions.setSearchQuery}
                        autoFocus={userMedia.isDesktop && !offline}
                        placeholder='Search Manuals, Components, Videos'
                        variant='unified'
                        disabled={offline}
                      />
                    </div>
                    {showBreadcrumb && <BreadCrumb items={breadcrumbs} variant='browse-guide' />}
                    {!searchQuery && showOfflineSaveBanner && (
                      <OfflineSaveBanner
                        vehicleId={sessionVehicleId}
                        contents={contents}
                        ownersManualPdfs={ownersManualPdfs}
                        vehicleInfo={this.props.vehicleInfo}
                        downloadState={downloadState}
                        onDownloadComplete={this.handleDownloadComplete}
                      />
                    )}
                  </div>
                  <div className='BrowseGuide-list'>
                    {this.renderSearchResults()}
                    {!searchQuery && (showChildren
                      ? (typeof children === 'function'
                        ? children({
                          downloadedCategories,
                          offlineVehicleId: this.getOfflineVehicleId(),
                          onRemoveDownloadedCategory: this.handleRemoveDownloadedCategory,
                          refreshOfflineCategories: this.refreshOfflineCategories,
                        })
                        : children)
                      : <Loader className='inverse' />)}
                  </div>
                </div>
              </ScreenContainer>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

function mapStateToProps({ contents, contents: { searchTopics }, vehicle, user }) {
  const { BROWSE_ROOT_KEY } = getConfig()
  const {
    fetching,
    searchQuery,
    errorSearch,
    searchFolders,
    searchContents,
    folders,
    foldersIndex,
    publications,
  } = contents
  const { currentId, vehicles, currentBrand, currentYear, currentModel, currentProductType, currentProductSubtype } =
    vehicle
  const { media: userMedia } = user
  const brand = BRANDS.find(item => item.name === currentBrand)

  let vehicleInfo = null
  if (currentId) {
    vehicleInfo = vehicles?.find(item => item.id === currentId) || null
    if (!vehicleInfo) {
      vehicleInfo = {
        id: currentId,
        make: currentBrand,
        year: currentYear,
        model: currentModel,
        FOREST_RIVER_PRODUCT_TYPE: currentProductType,
        FOREST_RIVER_PRODUCT_SUB_TYPE: currentProductSubtype,
      }
    }
  }

  const rootFolderResourceKey = getRootFolderResourceKey(
    publications,
    DEFAULT_PUBLICATION_TYPE,
    foldersIndex,
    BROWSE_ROOT_KEY
  )

  let processedSearchTopics = searchTopics
  if (processedSearchTopics) {
    processedSearchTopics = getCollapsedSearchResults(processedSearchTopics, searchFolders, folders, foldersIndex)
  }

  const searchResults = (searchContents && searchContents.filter(isSupportedContent)) || []

  return {
    fetching,
    currentId,
    currentBrand,
    brandRoute: brand ? `/${brand.key}` : '/',
    vehicleInfo,
    searchQuery,
    searchTopics: processedSearchTopics,
    errorSearch,
    searchResults,
    userMedia,
    foldersIndex,
    rootFolderResourceKey,
    contents,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    ContentsActions: bindActionCreators(ContentsActions, dispatch),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(BrowseGuideLayout))
